import React from "react";

// Chart colour roles for the Seller Radar charts, defined once and swapped for
// dark mode in one place. Every chart is written against these roles, never
// raw hex.
//
// Validated with the dataviz skill's validator against THIS app's surfaces
// (#ffffff light, #161d28 dark), not the reference ones:
//   series 1/2 (blue / orange)  categorical, both modes: all checks pass,
//                               worst CVD ΔE 24.7 light / 26.8 dark
//   then / now (one hue, two shades, the before -> after dumbbell): ordinal,
//                               both modes pass; "now" is always the more
//                               prominent shade on its surface
//
// Text never wears these colours: labels and values stay in the page's own
// text tokens (--c-900 / --c-700 / --c-500).
const CSS = `
.sr-viz {
  --sr-series-1: #2a78d6;
  --sr-series-2: #eb6834;
  --sr-then: #86b6ef;
  --sr-now: #1c5cab;
  --sr-grid: #e8e8e4;
  --sr-axis: #c3c2b7;
  --sr-surface: var(--surface, #ffffff);
  --sr-good-text: #006300;
  --sr-bad-text: #b91c1c;
  /* Status, not series: the "on target" and "miss" bands and the marks that
     say better / worse. Always shipped beside an icon, a word or a legend. */
  --sr-good-mark: #16a34a;
  --sr-bad-mark: #d03b3b;
  --sr-good-line: rgba(22, 163, 74, .55);
  --sr-good-wash: rgba(22, 163, 74, .07);
  --sr-bad-wash: rgba(208, 59, 59, .06);
  --sr-good-wash-key: rgba(22, 163, 74, .22);
  --sr-bad-wash-key: rgba(208, 59, 59, .22);
  --sr-select: rgba(42, 120, 214, .10);
}
:root[data-theme="dark"] .sr-viz {
  --sr-series-1: #3987e5;
  --sr-series-2: #d95926;
  --sr-then: #256abf;
  --sr-now: #86b6ef;
  --sr-grid: #262d3a;
  --sr-axis: #3a4252;
  --sr-good-text: #34d399;
  --sr-bad-text: #f26d6d;
  --sr-good-mark: #34d399;
  --sr-bad-mark: #f26d6d;
  --sr-good-line: rgba(52, 211, 153, .5);
  --sr-good-wash: rgba(52, 211, 153, .07);
  --sr-bad-wash: rgba(242, 109, 109, .07);
  --sr-good-wash-key: rgba(52, 211, 153, .25);
  --sr-bad-wash-key: rgba(242, 109, 109, .25);
  --sr-select: rgba(57, 135, 229, .16);
}
.sr-viz .sr-tip {
  position: absolute; pointer-events: none; z-index: 5;
  background: var(--surface); border: 1px solid var(--c-200); border-radius: 8px;
  padding: 7px 9px; font-size: 11.5px; color: var(--c-700);
  box-shadow: 0 6px 18px rgba(0,0,0,.18); white-space: nowrap;
}
.sr-viz .sr-tip strong { color: var(--c-900); font-weight: 700; }
.sr-viz .sr-hit:focus { outline: none; }
.sr-viz .sr-hit:focus-visible { outline: 2px solid var(--blue-600); outline-offset: 2px; border-radius: 4px; }
.sr-viz table.sr-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 8px; }
.sr-viz table.sr-table th, .sr-viz table.sr-table td {
  text-align: left; padding: 4px 6px; border-bottom: 1px solid var(--c-200); color: var(--c-700);
}
.sr-viz table.sr-table td.num, .sr-viz table.sr-table th.num { text-align: right; font-variant-numeric: tabular-nums; }
`;

export function VizStyles() {
  return <style>{CSS}</style>;
}

// A short line key for legends and tooltip rows: the mark carries identity,
// the text beside it stays in text ink.
export function LineKey({ color, dashed = false }) {
  return (
    <svg width="14" height="8" aria-hidden="true" style={{ flexShrink: 0 }}>
      <line x1="1" y1="4" x2="13" y2="4" stroke={color} strokeWidth="2.5" strokeLinecap="round"
            strokeDasharray={dashed ? "3 3" : undefined} />
    </svg>
  );
}

export function SwatchKey({ color, hollow = false }) {
  return (
    <span aria-hidden="true" style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2,
                                      flexShrink: 0, background: hollow ? "transparent" : color,
                                      border: `2px solid ${color}` }} />
  );
}
